/**
 * The app-level retry banner.
 *
 * When the API answers `503 database_unavailable` the failure is not this screen's
 * problem — the whole product is temporarily read-broken — so the notice belongs at
 * the shell, above whatever route happens to be mounted, with one Retry that refetches
 * everything rather than a dozen per-card error states.
 *
 * Detection is passive: the banner subscribes to the query cache and looks at the
 * errors that are already there. No screen has to report its failure upward.
 */
import { useQueryClient, type QueryCache } from "@tanstack/react-query";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { useCallback, useState, useSyncExternalStore } from "react";
import { isApiError } from "@/lib/api";
import { useSession } from "@/lib/session";

/**
 * Why the whole product is unusable, or null when it isn't.
 *
 * `offline` outranks `database`: a request that never left the browser
 * (`ApiError.status === 0`) says nothing about the database, and telling someone their
 * server has a database problem when their wifi dropped sends them to the wrong place.
 */
export type OutageReason = "offline" | "database";

function outageReasonFor(error: unknown): OutageReason | null {
  if (!isApiError(error)) return null;
  if (error.status === 0) return "offline";
  if (error.code === "database_unavailable" || error.status === 503) return "database";
  return null;
}

/**
 * The worst thing wrong with the cache right now — the whole of the banner's state,
 * derived from the cache rather than mirrored into React state.
 *
 * `offline` wins outright, so the first one found ends the scan.
 */
function readOutage(cache: QueryCache): OutageReason | null {
  let found: OutageReason | null = null;
  for (const query of cache.getAll()) {
    const next = outageReasonFor(query.state.error);
    if (next === "offline") return "offline";
    if (next) found = next;
  }
  return found;
}

/**
 * Watches every query in the cache for a whole-service failure. Returns to null as
 * soon as a retry succeeds, so the banner clears itself.
 *
 * The query cache is an external store, so it is read with `useSyncExternalStore`
 * rather than mirrored into `useState` from a subscription. That is not a style
 * preference — the mirror was a live defect. React Query notifies its cache
 * subscribers **synchronously**, and one of the things that notifies them is
 * `QueryCache.build()`, which is what a mounting `useQuery` does *from a component's
 * render body*. A `setState` driven by that notification therefore schedules an
 * update on this component while a different one is mid-render, which React reports
 * as "Cannot update a component (ErrorBanner) while rendering a different component".
 * The old functional-update guard could not prevent it: React emits the warning from
 * `scheduleUpdateOnFiber`, and `useState`'s bail-out-on-equal-value only skips that
 * call when the fiber happens to have no pending lanes.
 *
 * Two properties are wanted, and they need the two halves below.
 *
 *  · **Read from the store, don't copy it.** `useSyncExternalStore` calls
 *    `getSnapshot` during every render and compares the result with `Object.is`
 *    itself, so there is no second copy of the truth to fall out of step, a change
 *    that lands between subscribing and rendering is simply picked up instead of
 *    scheduled, an unchanged cache never reaches the scheduler at all, and the value
 *    cannot tear across a concurrent render.
 *
 *  · **Notify off the render stack.** `useSyncExternalStore` does not help with a
 *    notification that genuinely changes the snapshot mid-render: React's own
 *    `forceStoreRerender` goes through the same `scheduleUpdateOnFiber` and warns
 *    just the same (verified — see `src/tests/error-banner.test.tsx`). Nothing
 *    React-side can fix that, because the caller is writing to the cache during
 *    render; the write has to be observed afterwards. So the cache subscription
 *    hands `onStoreChange` to a microtask. Deferring loses nothing: React re-reads
 *    the snapshot on any render that happens first, and a microtask resolves before
 *    paint, so "the banner clears the moment a retry succeeds" still holds to the
 *    frame. `startTransition` would not do — the warning is emitted before lanes are
 *    consulted, so a transition warns exactly as loudly.
 *
 * The snapshot has to be referentially stable while the cache is unchanged, or React
 * re-renders forever comparing it. That is affordable here only because the snapshot
 * is `"offline" | "database" | null` — a value, not an object. Anything richer (an
 * object naming the failing query, say) would have to be memoised against the cache's
 * own state before `getSnapshot` could return it.
 */
export function useOutageReason(): OutageReason | null {
  const client = useQueryClient();

  // Both callbacks are keyed to the client: React re-subscribes when `subscribe`
  // changes identity and re-runs a store effect when `getSnapshot` does, so fresh
  // closures on every render would churn for nothing.
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      // `queued` collapses a burst of cache events into one wake-up; `live` drops a
      // wake-up whose component unmounted between the event and the microtask.
      let live = true;
      let queued = false;
      const unsubscribe = client.getQueryCache().subscribe(() => {
        if (queued) return;
        queued = true;
        queueMicrotask(() => {
          queued = false;
          if (live) onStoreChange();
        });
      });
      return () => {
        live = false;
        unsubscribe();
      };
    },
    [client],
  );
  const getSnapshot = useCallback(() => readOutage(client.getQueryCache()), [client]);

  return useSyncExternalStore(subscribe, getSnapshot);
}

/** Kept for callers that only need the boolean. */
export function useServiceUnavailable(): boolean {
  return useOutageReason() !== null;
}

const OUTAGE_TEXT: Record<OutageReason, string> = {
  offline:
    "BotpressoHR can’t reach the server. Check your connection — nothing you’ve saved is lost.",
  database:
    "BotpressoHR can’t reach its database right now. Nothing you’ve saved is lost — try again in a moment.",
};

export function ErrorBanner() {
  const client = useQueryClient();
  const { error: sessionError, refresh } = useSession();
  const queryReason = useOutageReason();
  const [retrying, setRetrying] = useState(false);

  const reason = queryReason ?? outageReasonFor(sessionError);
  if (!reason) return null;

  const retry = async () => {
    setRetrying(true);
    try {
      await Promise.all([refresh(), client.refetchQueries({ type: "active" })]);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div
      role="alert"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "11px 24px",
        background: "var(--red-wash)",
        borderBottom: "1px solid var(--danger-line)",
        fontFamily: "var(--font-body)",
        fontSize: 12.5,
        color: "var(--ink)",
        flexWrap: "wrap",
      }}
    >
      <AlertTriangle size={16} color="var(--red)" aria-hidden="true" />
      <span style={{ flex: 1, minWidth: 220, lineHeight: 1.5 }}>{OUTAGE_TEXT[reason]}</span>
      <button
        type="button"
        onClick={() => void retry()}
        disabled={retrying}
        style={{
          cursor: retrying ? "not-allowed" : "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontFamily: "var(--font-body)",
          fontWeight: 700,
          fontSize: 11.5,
          padding: "6px 11px",
          borderRadius: 8,
          border: "1px solid var(--danger-line)",
          background: "var(--surface)",
          color: "var(--red)",
          opacity: retrying ? 0.6 : 1,
        }}
      >
        <RotateCcw size={12} aria-hidden="true" /> {retrying ? "Retrying…" : "Retry"}
      </button>
    </div>
  );
}

export default ErrorBanner;
